import {
  AUTOCLICK_TYPE_COMPLETED_DELAY,
  AUTOCLICK_TYPE_INTERACTION_COUNT,
  AUTOCLICK_TYPE_LAST_INTERACTION,
  AUTOCLICK_TYPE_LAST_INTERACTION_AND_INTERACTION_COUNT,
  AUTOCLICK_TYPE_LOAD_DELAY,
  AUTOCLICK_TYPE_VIEWABLE_DELAY,
  AUTOCLICK_TYPE_VIEWABLE_DELAY_WITH_RESET,
} from "../../constants";
import AutoclickBehaviorCompletedDelay from "./behaviors/AutoclickBehaviorCompletedDelay";
import AutoclickBehaviorInteractionCount from "./behaviors/AutoclickBehaviorInteractionCount";
import AutoclickBehaviorLastInteraction from "./behaviors/AutoclickBehaviorLastInteraction";
import AutoclickBehaviorLastInteractionAndCount from "./behaviors/AutoclickBehaviorLastInteractionAndCount";
import AutoclickBehaviorLoadDelay from "./behaviors/AutoclickBehaviorLoadDelay";
import AutoclickBehaviorViewableDelay from "./behaviors/AutoclickBehaviorViewableDelay";
import AutoclickBehaviorViewableDelayWithReset from "./behaviors/AutoclickBehaviorViewableDelayWithReset";

const behaviorClasses = {
  [AUTOCLICK_TYPE_COMPLETED_DELAY]: AutoclickBehaviorCompletedDelay,
  [AUTOCLICK_TYPE_INTERACTION_COUNT]: AutoclickBehaviorInteractionCount,
  [AUTOCLICK_TYPE_LAST_INTERACTION]: AutoclickBehaviorLastInteraction,
  [AUTOCLICK_TYPE_LAST_INTERACTION_AND_INTERACTION_COUNT]:
    AutoclickBehaviorLastInteractionAndCount,
  [AUTOCLICK_TYPE_LOAD_DELAY]: AutoclickBehaviorLoadDelay,
  [AUTOCLICK_TYPE_VIEWABLE_DELAY]: AutoclickBehaviorViewableDelay,
  [AUTOCLICK_TYPE_VIEWABLE_DELAY_WITH_RESET]:
    AutoclickBehaviorViewableDelayWithReset,
};

const MONITORED_EVENTS = {
  INTERACTION_EVENT: "interactionEvent",
  COMPLETED: "completed",
  ENGAGEMENT: "engagement",
  HTML_LOADED: "htmlLoaded",
  VIEWABLE: "viewable",
  VIEWABLE_CHANGE: "viewableChange",
};

const AUTOCLICK_EVENT_COUNT_CHANGE = "countChanged";

/**
 * @callback ClickthroughHandler
 * @param  {object} lastEvent - The last Interaction event.
 */

/**
 * @typedef AutoclickBehaviorConfig
 * @property {string} type - autoclick variant type.
 * @property {number} delay - In ms. Time to wait until opening App Store from
 * autoclick click.
 * @property {number} count - Max occurrences of events as determined by type.
 */

/**
 * Controls App Store On Interaction, aka. autoclick, behavior.
 * @see https://docs.google.com/document/d/1QYHzXt1oxmGIx2F63XErz7dF4SD6pxSi-1mkxxnHsUU/edit#heading=h.bypyxke51ec
 */
class Autoclick {
  /**
   *
   * @param {AutoclickBehaviorConfig} behavior - Describes the autoclick behavior.
   * @param {Object} ctx
   * @param {ClickthroughHandler} ctx.clickthroughHandler
   * @returns
   */
  constructor(behavior, { clickthroughHandler }) {
    if (behavior == null) {
      this.disable();
      return;
    }
    this._clickthroughHandler = clickthroughHandler;
    this._lastCount = 0;
    this._lastInteraction = null;

    this._behavior = behavior;
    const behaviorType = behavior == null ? null : behavior.type;
    if (behaviorClasses[behaviorType] == null) {
      console.error(`Behavior class not found: ${behaviorType}`);
      this.disable();
      return;
    }
    /** @type {AutoclickBehaviorInteractionCount|AutoclickBehaviorCompletedDelay|AutoclickBehaviorLastInteraction|AutoclickBehaviorLastInteractionAndCount} */
    this.behaviorClass = new behaviorClasses[behaviorType]({
      autoclick: this,
      behavior,
      delayClickthroughHandler: this._handleDelayClickthrough.bind(this),
    });

    this._handleDelayClickthrough = this._handleDelayClickthrough.bind(this);

    this.eventListeners = {
      [MONITORED_EVENTS.ENGAGEMENT]: [
        this.behaviorClass.onEngagement.bind(this.behaviorClass),
      ],
      [MONITORED_EVENTS.HTML_LOADED]: [
        this.behaviorClass.onHtmlLoaded.bind(this.behaviorClass),
      ],
      [MONITORED_EVENTS.VIEWABLE]: [
        this.behaviorClass.onViewable.bind(this.behaviorClass),
      ],
      [MONITORED_EVENTS.VIEWABLE_CHANGE]: [
        this.behaviorClass.onViewableChange.bind(this.behaviorClass),
      ],
      [MONITORED_EVENTS.COMPLETED]: [
        this.behaviorClass.onCompleted.bind(this.behaviorClass),
      ],
      [MONITORED_EVENTS.INTERACTION_EVENT]:
        // Order matters.
        [
          this._recordLastInteraction.bind(this),
          this.behaviorClass.onInteraction.bind(this.behaviorClass),
        ],
      [AUTOCLICK_EVENT_COUNT_CHANGE]: [],
    };
  }

  disable() {
    this.autoclickDisabled = true;
  }

  emitInteractionEvent(...args) {
    this.emit(MONITORED_EVENTS.INTERACTION_EVENT, ...args);
  }

  emitInteractionPromptReachedEvent(...args) {
    this.emit(MONITORED_EVENTS.COMPLETED, ...args);
  }

  emitHtmlLoadedEvent(...args) {
    this.emit(MONITORED_EVENTS.HTML_LOADED, ...args);
  }

  emitViewableEvent(...args) {
    this.emit(MONITORED_EVENTS.VIEWABLE, ...args);
  }

  emitViewableChangeEvent(...args) {
    this.emit(MONITORED_EVENTS.VIEWABLE_CHANGE, ...args);
  }

  _recordLastInteraction(e) {
    this._lastInteraction = e;
  }

  _handleDelayClickthrough(delay) {
    clearTimeout(this._delayTimer);
    if (this.autoclickDisabled) return;

    this._delayTimer = setTimeout(() => {
      this._clickthrough();
    }, delay || 0);
  }

  _clickthrough() {
    if (this.autoclickDisabled) return;
    this._clickthroughHandler(this._lastInteraction);
    this.disable();
  }

  emit(eventName, ...args) {
    if (this.autoclickDisabled) return;
    if (!(eventName in this.eventListeners)) {
      throw new Error(`Event ${eventName} not found!`);
    }
    this.eventListeners[eventName].forEach((f) => {
      f(...args);
    });
  }
}

export default Autoclick;



// WEBPACK FOOTER //
// ./src/js/madlib/autoclick/Autoclick.js